import React from 'react';
import { Container, Box, Grid, Typography } from '@mui/material';
import { theme } from '../../../constants/theme';
import { CompanyHeader } from '../../CompanyComponents/CompanyHeader';
import { CompanyOverview } from '../../CompanyComponents/CompanyOverview';
import { CompanyTabs } from '../../CompanyComponents/CompanyTabs';
import { CompanyContainerSkeleton } from './CompanyContainerSkeleton';
import { ICompanyDetails } from '../../../redux/company/types';
import { useThemeContext } from '../../../hook/useThemeManager';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { CompanySidebar } from '../../CompanyComponents';

/** Match `AuditionWrapper` content column: padded below full-bleed hero. */
const PAGE_CONTENT_PX = { xs: 3, sm: 3.5, md: 4, lg: 5, xl: 5 };
const PAGE_CONTENT_MAX = 1120;

interface ICompanyContainer {
    companyDetails?: ICompanyDetails;
    loading?: boolean;
    onEditClick?: () => void;
}

const CompanyContainer: React.FC<ICompanyContainer> = ({ companyDetails, loading = false, onEditClick }) => {
    const { themeStyle } = useThemeContext();
    if (loading) {
        return <CompanyContainerSkeleton />;
    }

    if (!loading && !companyDetails?.company) {
        return (
            <Container
                maxWidth={false}
                sx={{
                    display: 'flex',
                    width: '100vw',
                    height: '60vh',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}>
                <Typography sx={{ color: theme[themeStyle].tag_white, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                    Company not found
                </Typography>
            </Container>
        );
    }

    return (
        <Container
            disableGutters
            maxWidth={false}
            style={{
                height: 'auto',
                justifyContent: 'flex-start',
                display: 'flex',
                flexDirection: 'column',
            }}
            sx={{
                alignItems: 'stretch',
                width: '100%',
                marginLeft: 0,
                paddingTop: 0,
                paddingBottom: { xs: 4, sm: 6 },
                paddingLeft: 0,
                paddingRight: 0,
                overflowX: 'hidden',
                minHeight: '70vh',
                backgroundColor: theme[themeStyle].pure_black,
                minWidth: 0,
                boxSizing: 'border-box',
            }}>
            <CompanyHeader companyDetails={companyDetails} onEditClick={onEditClick} />
            <Box
                sx={{
                    width: '100%',
                    maxWidth: PAGE_CONTENT_MAX,
                    mx: 'auto',
                    px: PAGE_CONTENT_PX,
                    pt: { xs: 2, sm: 3 },
                    pb: { xs: 2, sm: 3 },
                    boxSizing: 'border-box',
                    minWidth: 0,
                }}>
                <Grid
                    container
                    spacing={{ xs: 3, lg: 4 }}
                    sx={{
                        mt: 0,
                        mb: 2,
                        '@media (width: 344px) and (height: 882px)': {
                            '--Grid-columnSpacing': '12px',
                            '--Grid-rowSpacing': '24px',
                        },
                    }}>
                    <Grid item xs={12} lg={8} sx={{ minWidth: 0 }}>
                        <CompanyOverview companyDetails={companyDetails} />
                        <CompanyTabs companyDetails={companyDetails} />
                    </Grid>
                    <Grid item xs={12} lg={4} sx={{ minWidth: 0 }}>
                        <CompanySidebar companyDetails={companyDetails} />
                    </Grid>
                </Grid>
            </Box>
        </Container>
    );
};

export { CompanyContainer };
